import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileText, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel, StatusBadge } from "@/components/app/common";
import { IconBtn } from "@/components/app/IconAction";
import { useMe } from "@/components/app/MeContext";
import { EmailQuoteDialog } from "@/components/sales/EmailQuote";
import { useFetch } from "@/hooks/useFetch";
import { api, downloadFile, errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";

export const piFile = (p) => `Proforma_${(p.pi_no || "Draft").replace(/\//g, "_")}_${(p.customer_name || "").split(/\s+/).slice(0, 2).join("_").replace(/[^A-Za-z0-9_]/g, "")}.pdf`;
export const piDownload = (p) => downloadFile(`/proforma-invoices/${p.id}/pdf`, piFile(p)).catch((e) => toast.error(errMsg(e)));

export function emailCfg(p) {
  return {
    contactsUrl: `/proforma-invoices/${p.id}/email-contacts`, sendUrl: `/proforma-invoices/${p.id}/email`, title: p.pi_no,
    subject: `Proforma Invoice ${p.pi_no} - ${p.customer_name} (Quotation ${p.quote_ref})`,
    message: (c, sig) => `Dear ${p.contact_name || "Sir/Madam"},\n\nPlease find attached our proforma invoice ${p.pi_no} against quotation ${p.quote_ref}.\n\nRegards,\n${sig}\nTESSOL`,
  };
}

export function PiEmailDialog({ p, onClose, onSent }) {
  return <EmailQuoteDialog q={{ id: p.quote_id, ref: p.quote_ref }} cfg={emailCfg(p)} onClose={onClose} onSent={onSent || (() => {})} />;
}

// Quote detail: convert to proforma + every proforma made from this quotation
export function QuoteProformas({ q }) {
  const { data, reload } = useFetch(`/quotes/${q.id}/proformas?s=${q.status}&r=${encodeURIComponent(q.updated_at || "")}`);
  const { can } = useMe();
  const nav = useNavigate();
  const [mail, setMail] = useState(null);
  const make = async () => {
    try { const r = await api.post(`/quotes/${q.id}/proforma`); toast.success("Draft proforma invoice created"); nav(`/sales/proforma/${r.data.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const why = data?.block_reason;
  return (
    <Panel title="Proforma invoices" testid="quote-proformas-panel">
      {can("sales.edit") && <span title={why || ""}><Button size="sm" className="mb-2" disabled={!data || !!why} onClick={make} data-testid="quote-proforma-btn"><FileText className="h-4 w-4 mr-1" />Convert to Proforma Invoice</Button></span>}
      {why && <p className="mb-2 text-[11px] text-slate-500" data-testid="quote-proforma-block">{why}</p>}
      {(data?.rows || []).map((p) => (
        <div key={p.id} className="flex items-center gap-2 border-b border-slate-100 py-1.5 text-xs" data-testid={`quote-proforma-${p.id}`}>
          <Link to={`/sales/proforma/${p.id}`} className="font-mono text-sky-700 hover:underline">{p.pi_no || "Draft"}</Link>
          <span className="text-slate-500">{fmtDate(p.pi_date)}</span><StatusBadge status={p.status} />
          <span className="ml-auto font-mono">{inr(p.grand_total)}</span>{p.advance_pct ? <span className="text-slate-500">adv {p.advance_pct}%</span> : null}
          <IconBtn icon={Download} label="Download PDF" onClick={() => piDownload(p)} testid={`quote-proforma-dl-${p.id}`} />
          {p.status === "issued" && can("sales.edit") && <IconBtn icon={Mail} label="Send by email" onClick={() => setMail(p)} testid={`quote-proforma-mail-${p.id}`} />}
        </div>
      ))}
      {data && !data.rows.length && <p className="text-xs text-slate-400">No proforma invoices yet</p>}
      {mail && <PiEmailDialog p={mail} onClose={() => setMail(null)} onSent={reload} />}
    </Panel>
  );
}
